import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../components/Note";
import api from "../api";

// Vista de calendario mensual sencilla con tarjetas por día.
// Muestra campañas guardadas (campanas/) agrupadas por fecha (si fecha !== "inmediato").
// Cada tarjeta indica: nombre de campaña, lista objetivo (nombre y cantidad de contactos), y hora.

function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0,0,0,0);
  return x;
}

function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23,59,59,999);
  return x;
}

function getMonthMatrix(anchor) {
  // Devuelve 6 semanas (7 días cada una)
  const first = startOfMonth(anchor);
  const startWeekDay = (first.getDay() + 6) % 7; // lunes=0
  const start = new Date(first);
  start.setDate(first.getDate() - startWeekDay);
  const weeks = [];
  let cur = new Date(start);
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

export default function Calendario() {
  const navigate = useNavigate();
  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [campanas, setCampanas] = useState([]); // [{ id, nombre, clientes, fecha, plantillas }]
  const [listas, setListas] = useState([]); // [{ id, name, color, contacts: [ids] }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sentMap, setSentMap] = useState({}); // { 'YYYY-MM-DD': [campaignId,...] }

  const reload = async () => {
    let cancelled = false;
    try {
      setLoading(true);
      setError("");
      // Rango del mes actual
      const first = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
      const last = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
      const from = `${first.getFullYear()}-${String(first.getMonth()+1).padStart(2,'0')}-${String(first.getDate()).padStart(2,'0')}`;
      const to = `${last.getFullYear()}-${String(last.getMonth()+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
      const results = await Promise.allSettled([
        api.get("campanas/"),
        api.get("listas/"),
        api.get("campanas/sent-map/", { params: { from, to } })
      ]);
      if (!cancelled) {
        const cRes = results[0];
        const lRes = results[1];
        const sRes = results[2];
        if (cRes.status === 'fulfilled') setCampanas(Array.isArray(cRes.value?.data) ? cRes.value.data : []);
        if (lRes.status === 'fulfilled') setListas(Array.isArray(lRes.value?.data) ? lRes.value.data : []);
        if (sRes.status === 'fulfilled') setSentMap(sRes.value?.data || {});
        if (cRes.status === 'rejected' && lRes.status === 'rejected' && sRes.status === 'rejected') {
          setError("No se pudo cargar el calendario");
        }
      }
    } catch (e) {
      if (!cancelled) setError("No se pudo cargar el calendario");
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  };

  useEffect(() => {
    // Primera carga
    reload();
  }, []);

  // Cuando cambie el mes, recargar datos (incluye mapa de enviados)
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthAnchor]);

  const listsById = useMemo(() => {
    const m = new Map();
    (listas || []).forEach(l => m.set(String(l.id), l));
    return m;
  }, [listas]);

  const eventsByDate = useMemo(() => {
    // Agrupa campañas por clave YYYY-MM-DD, tolerando varios formatos de fecha en c.fecha.
  function normalize(str) {
      if (!str) return null;
      let raw = String(str).trim();
      if (!raw || raw.toLowerCase() === 'inmediato') return null;
      // Normalizar separadores y comas
      raw = raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  // separar fecha y hora (si hay)
      let datePart = raw;
      let timePart = '';
      // Soportar ISO con T
      const hadSlash = /\//.test(raw);
      if (/T/.test(raw)) {
        const [dIso, tIso] = raw.split('T');
        datePart = dIso; timePart = tIso || '';
      } else {
        const sp = raw.split(/\s+/);
        if (sp.length >= 2) { datePart = sp[0]; timePart = sp.slice(1).join(' '); }
      }
      datePart = datePart.replace(/,/g, '');
      // normalizar separadores
      const dClean = datePart.replace(/[\.\/]/g, '-');
      const seg = dClean.split('-');
      let y, m, d;
      const is4 = (s) => /^\d{4}$/.test(s);
      const toNum = (s) => Number(s || 0);
      if (seg.length === 3) {
        if (is4(seg[0])) { // YYYY-MM-DD
          y = toNum(seg[0]); m = toNum(seg[1]); d = toNum(seg[2]);
        } else if (is4(seg[2])) {
          // DD-MM-YYYY o MM-DD-YYYY: decidir por rango
          const a = toNum(seg[0]);
          const b = toNum(seg[1]);
          y = toNum(seg[2]);
          if (a > 12) { // DD-MM-YYYY
            d = a; m = b;
          } else if (b > 12) { // MM-DD-YYYY
            m = a; d = b;
          } else {
            // ambiguo: si el original tenía '/', preferir MM/DD/YYYY; si no, asumir DD-MM-YYYY
            if (hadSlash) { m = a; d = b; } else { d = a; m = b; }
          }
        } else if (/^\d{2}$/.test(seg[2])) {
          // DD-MM-YY o MM-DD-YY → asumir 20YY
          const a = toNum(seg[0]);
          const b = toNum(seg[1]);
          y = 2000 + toNum(seg[2]);
          if (a > 12) { d = a; m = b; } else if (b > 12) { m = a; d = b; } else { if (hadSlash) { m = a; d = b; } else { d = a; m = b; } }
        }
      }
      if (!y || !m || !d) {
        // Intentar formato español: "DD de <mes> de YYYY" (opcional hora al final)
        const months = {
          'enero':1,'febrero':2,'marzo':3,'abril':4,'mayo':5,'junio':6,
          'julio':7,'agosto':8,'septiembre':9,'setiembre':9,'sept':9,'sep':9,'set':9,
          'octubre':10,'oct':10,'oct.':10,
          'noviembre':11,'nov':11,'nov.':11,
          'diciembre':12,'dic':12,'dic.':12
        };
        const m1 = raw.toLowerCase().match(/(\d{1,2})\s+de\s+([a-záéíóúñ]+)\s+de\s+(\d{4})(.*)$/i);
        if (m1) {
          const dd = Number(m1[1]);
          const monKey = (m1[2] || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const mon = months[monKey] || months[m1[2]];
          let yyyy = Number(m1[3]);
          if (String(m1[3]).length === 2) yyyy = 2000 + yyyy;
          if (yyyy && mon && dd) {
            y = yyyy; m = mon; d = dd;
            const tail = (m1[4] || '').trim();
            if (tail && !timePart) timePart = tail; // conserva hora si venía al final
          }
        }
      }
      if (!y || !m || !d) {
        // Intentar parse nativo
        const tmp = new Date(raw);
        if (!isNaN(tmp.getTime())) {
          y = tmp.getFullYear(); m = tmp.getMonth() + 1; d = tmp.getDate();
          // reconstruir time si no vino explícita
          if (!timePart) {
            const hh = String(tmp.getHours()).padStart(2, '0');
            const mm = String(tmp.getMinutes()).padStart(2, '0');
            timePart = `${hh}:${mm}`;
          }
        }
      }
      if (!y || !m || !d) return null;
      const key = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      // normalizar hora (aceptar AM/PM)
      let time = '';
      let t = String(timePart || '').trim();
      // Normalizar variantes españolas 'a. m.' / 'p. m.'
      t = t.replace(/a\.?\s*m\.?/ig, 'am').replace(/p\.?\s*m\.?/ig, 'pm');
      if (t) {
        const ampm = /(am|pm)/i.test(t);
        let mt = t.match(/(\d{1,2}):(\d{2})/);
        if (!mt) {
          // permitir hora sin minutos ("9 am") → 09:00
          const hm = t.match(/\b(\d{1,2})\b/);
          if (hm) mt = [hm[0], hm[1], '00'];
        }
        if (mt) {
          let hh = Number(mt[1]);
          const mm = mt[2];
          if (ampm) {
            const isPM = /pm/i.test(t);
            if (isPM && hh < 12) hh += 12;
            if (!isPM && hh === 12) hh = 0;
          }
          time = `${String(hh).padStart(2,'0')}:${mm}`;
        }
      }
      return { key, time };
    }

    const map = new Map();
    (campanas || []).forEach(c => {
      const n = normalize(c.fecha_local || c.fecha);
      if (!n) return;
      const arr = map.get(n.key) || [];
      arr.push({ ...c, _date: n.key, _time: n.time || '' });
      map.set(n.key, arr);
    });
    return map; // Map<YYYY-MM-DD, Array<campaña>>
  }, [campanas]);

  const matrix = useMemo(() => getMonthMatrix(monthAnchor), [monthAnchor]);
  const month = monthAnchor.getMonth();
  const year = monthAnchor.getFullYear();

  const monthName = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(monthAnchor);

  const goPrev = () => {
    const d = new Date(monthAnchor);
    d.setMonth(d.getMonth() - 1);
    setMonthAnchor(d);
  };
  const goNext = () => {
    const d = new Date(monthAnchor);
    d.setMonth(d.getMonth() + 1);
    setMonthAnchor(d);
  };
  const goToday = () => setMonthAnchor(new Date());

  return (
    <div className="dashboard-layout" style={{ background: "#fff", minHeight: "100vh" }}>
      <Sidebar />
      <main className="dashboard-main" style={{ background: "#fff", color: "#0b1220", minHeight: "100vh" }}>
        <div className="dashboard-header" style={{ color: "#0b1220", fontSize: "1.7rem", fontWeight: 600, marginBottom: "1rem", display: 'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span>Calendario de campañas</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={goPrev} style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1', background:'#fff', cursor:'pointer' }}>◀</button>
            <button onClick={goToday} style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1', background:'#fff', cursor:'pointer' }}>Hoy</button>
            <button onClick={goNext} style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1', background:'#fff', cursor:'pointer' }}>▶</button>
            <button onClick={reload} style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #22c55e', background:'#22c55e', color:'#0b1220', cursor:'pointer', fontWeight:600 }}>Recargar</button>
          </div>
        </div>
        <div style={{ marginBottom: 16, color:'#64748b' }}>{monthName.charAt(0).toUpperCase() + monthName.slice(1)}</div>
        {loading ? (
          <div style={{ padding: 12 }}>Cargando…</div>
        ) : error ? (
          <div style={{ padding: 12, color:'#dc2626' }}>{error}</div>
        ) : (
          <section style={{ background:'#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(44,62,80,0.18)', padding: 12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap: 8, marginBottom: 8, color:'#64748b', fontSize:13 }}>
              {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(h => (<div key={h} style={{ textAlign:'center' }}>{h}</div>))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap: 8 }}>
              {matrix.flat().map((d, idx) => {
                const y = d.getFullYear();
                const m = d.getMonth();
                const dd = d.getDate();
                const inMonth = m === month && y === year;
                const key = `${y}-${String(m+1).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
                const items = eventsByDate.get(key) || [];
                return (
                  <div key={idx} onClick={() => { const dayKey = key; // YYYY-MM-DD
                    // Prefijar fecha en página Mensajes
                    try { localStorage.setItem('prefCalDate', dayKey); } catch {}
                    navigate('/mensajes');
                  }}
                    style={{ border:'1px solid #e2e8f0', borderRadius: 10, padding: 8, background: inMonth ? '#fff' : '#f8fafc', minHeight: 110, display:'flex', flexDirection:'column', cursor:'pointer' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, color: inMonth ? '#0b1220' : '#94a3b8' }}>{dd}</div>
                      {items.length > 0 && (
                        <div style={{ fontSize:10, background:'#e2e8f0', color:'#0b1220', borderRadius:999, padding:'2px 6px' }}>{items.length}</div>
                      )}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {items.length === 0 && (
                        <div style={{ fontSize:12, color:'#94a3b8' }}>—</div>
                      )}
                      {items.map(ev => {
                        const list = listsById.get(String(ev.clientes));
                        const count = Array.isArray(list?.contacts) ? list.contacts.length : (typeof list?.contacts_count === 'number' ? list.contacts_count : undefined);
                        const color = list?.color || '#e2e8f0';
                        const hour = ev._time || '';
                        const handleOpen = (e) => {
                          e.stopPropagation();
                          try { localStorage.setItem('prefSelectedCampaignId', String(ev.id)); localStorage.setItem('prefCalDate', key); } catch {}
                          navigate('/mensajes');
                        };
                        const handleDelete = async (e) => {
                          e.stopPropagation();
                          if (!confirm('¿Eliminar esta campaña programada?')) return;
                          try {
                            await api.delete(`campanas/delete/${ev.id}/`);
                            setCampanas((prev) => prev.filter((c) => String(c.id) !== String(ev.id)));
                          } catch (err) {
                            console.error('Error eliminando campaña', err);
                            alert('No se pudo eliminar la campaña');
                          }
                        };
                        const sentIds = sentMap[key] || [];
                        const isSent = sentIds.some((cid) => String(cid) === String(ev.id));
                        const cardStyle = {
                          display:'flex', alignItems:'center', gap:8,
                          border:'1px solid ' + (isSent ? '#86efac' : '#e2e8f0'),
                          borderRadius:8, padding:'6px 8px',
                          background: isSent ? '#dcfce7' : '#f8fafc'
                        };
                        return (
                          <div key={`ev-${ev.id}`} title={String(ev.fecha || '')} onClick={handleOpen} style={cardStyle}>
                            <span title={list?.name || ''} style={{ width:10, height:10, borderRadius:999, background: color, display:'inline-block', flex:'0 0 10px', border:'1px solid #cbd5e1' }} />
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:12, color:'#0b1220', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ev.nombre}</div>
                              <div style={{ fontSize:11, color:'#64748b' }}>{list?.name || 'Lista'}{typeof count === 'number' ? ` · ${count} contactos` : ''}{hour ? ` · ${hour}` : ''}</div>
                            </div>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={handleOpen} title="Abrir en Mensajes" style={{ background:'#e2e8f0', color:'#0b1220', border:'none', borderRadius:6, padding:'2px 6px', fontSize:11, cursor:'pointer' }}>Abrir</button>
                              <button onClick={handleDelete} title="Eliminar campaña" style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:6, padding:'2px 6px', fontSize:11, cursor:'pointer' }}>Borrar</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
